import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, ArrowLeft, CalendarDays, Download, Calendar, Copy, RefreshCw, Check, FileText, Filter, Home, KeyRound, Save, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEPT_COLORS = {
  Medicine: { bg: "bg-emerald-200", text: "text-emerald-900", border: "border-emerald-300" },
  Surgery: { bg: "bg-blue-200", text: "text-blue-900", border: "border-blue-300" },
  Pediatrics: { bg: "bg-purple-200", text: "text-purple-900", border: "border-purple-300" },
  "OB/GYN": { bg: "bg-pink-200", text: "text-pink-900", border: "border-pink-300" },
  General: { bg: "bg-amber-200", text: "text-amber-900", border: "border-amber-300" },
  Dental: { bg: "bg-teal-200", text: "text-teal-900", border: "border-teal-300" },
  Ophthalmology: { bg: "bg-indigo-200", text: "text-indigo-900", border: "border-indigo-300" },
  // Phase 60.2 — distinct palette for non-medical (Phase 57) approved guests
  // so coordinators can spot them instantly on the calendar.
  "Non-Medical": { bg: "bg-stone-200", text: "text-stone-900", border: "border-stone-400" },
};

// Phase 50 — visual coding for trainee level (renders as a left border on each chip)
const LEVEL_STRIPE = {
  student: "border-l-4 border-l-amber-600",
  resident: "border-l-4 border-l-blue-700",
  faculty: "border-l-4 border-l-emerald-700",
};

const visitorLevel = (v) => (v.is_student ? "student" : v.is_resident ? "resident" : "faculty");
const LEVEL_LABEL = { student: "Student", resident: "Resident", faculty: "Faculty" };

const STATUS_DOT = {
  approved: "bg-emerald-500",
  pending_housing: "bg-purple-500",
  pending_cmo: "bg-blue-500",
  pending_department: "bg-amber-500",
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function dateToStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isDateInRange(dateStr, arrival, departure) {
  return dateStr >= arrival && dateStr <= departure;
}

export default function VisitorCalendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  // Phase 50 filters
  const [filterDept, setFilterDept] = useState("all");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [filterTrainee, setFilterTrainee] = useState("all");
  const [downloadingList, setDownloadingList] = useState(false);
  // Phase 56 — leases overlay so coordinators see when houses are tied up
  const [leases, setLeases] = useState([]);
  const [showLeases, setShowLeases] = useState(false);
  // Phase 56 — buffered date changes from drag-and-drop. Map<request_id, {arrival_date, departure_date}>
  const [pendingMoves, setPendingMoves] = useState({});
  const [saving, setSaving] = useState(false);
  const pendingCount = Object.keys(pendingMoves).length;

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => {});
  }, []);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.set("month", `${year}-${String(month + 1).padStart(2, "0")}`);
    if (filterDept && filterDept !== "all") params.set("department", filterDept);
    if (filterSpecialty.trim()) params.set("specialty", filterSpecialty.trim());
    if (filterTrainee && filterTrainee !== "all") params.set("trainee", filterTrainee);
    return params.toString();
  }, [year, month, filterDept, filterSpecialty, filterTrainee]);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calendar/with-leases?${buildQueryString()}`);
      setVisitors(data.visitors || []);
      setLeases(data.leases || []);
      setPendingMoves({});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [buildQueryString]);

  // Phase 56 — overlay pending date moves on top of the server snapshot so
  // the grid reflects the drag immediately without re-fetching.
  const visitorsWithPending = visitors.map((v) => {
    const m = pendingMoves[v.id];
    return m ? { ...v, ...m, _pending: true } : v;
  });

  // Drag-and-drop handlers. Drag the chip onto any day cell to shift the
  // arrival to that day (preserving the stay length).
  const onChipDragStart = (e, v) => {
    e.dataTransfer.setData("application/x-visitor", JSON.stringify({ id: v.id, arrival: v.arrival_date, departure: v.departure_date }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onCellDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onCellDrop = (e, dayStr) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/x-visitor");
    if (!raw || !dayStr) return;
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    const arrival = new Date(payload.arrival);
    const departure = new Date(payload.departure);
    const stayDays = Math.round((departure - arrival) / (1000 * 60 * 60 * 24));
    const newArrival = new Date(`${dayStr}T00:00:00`);
    const newDeparture = new Date(newArrival);
    newDeparture.setDate(newDeparture.getDate() + stayDays);
    const ad = newArrival.toISOString().slice(0, 10);
    const dd = newDeparture.toISOString().slice(0, 10);
    if (ad === payload.arrival && dd === payload.departure) return;
    setPendingMoves((prev) => ({ ...prev, [payload.id]: { arrival_date: ad, departure_date: dd } }));
    toast.info(`Moved to ${ad} → ${dd} (unsaved)`);
  };

  const discardPending = () => {
    if (pendingCount === 0) return;
    if (!window.confirm(`Discard ${pendingCount} unsaved move${pendingCount === 1 ? "" : "s"}?`)) return;
    setPendingMoves({});
    toast.info("Pending moves discarded");
  };

  const savePending = async () => {
    if (pendingCount === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    await Promise.all(Object.entries(pendingMoves).map(async ([rid, patch]) => {
      try {
        await api.put(`/requests/${rid}/edit`, { arrival_date: patch.arrival_date, departure_date: patch.departure_date });
        ok++;
      } catch { fail++; }
    }));
    if (fail === 0) toast.success(`Saved ${ok} move${ok === 1 ? "" : "s"}`);
    else toast.error(`${ok} saved · ${fail} failed — reloading`);
    setSaving(false);
    fetchCalendar();
  };

  // Browser-level guard for unsaved moves
  useEffect(() => {
    if (pendingCount === 0) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [pendingCount]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const [downloading, setDownloading] = useState(false);

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeUrl, setSubscribeUrl] = useState("");
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const buildAbsoluteUrl = (pathOrUrl) => {
    if (!pathOrUrl) return "";
    if (pathOrUrl.startsWith("http")) return pathOrUrl;
    const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return origin.replace(/\/$/, "") + pathOrUrl;
  };

  const openSubscribeDialog = async () => {
    setSubscribeOpen(true);
    setSubscribeLoading(true);
    try {
      const { data } = await api.get("/calendar/subscription-url");
      setSubscribeUrl(buildAbsoluteUrl(data.url || data.path));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not fetch subscription URL");
      setSubscribeOpen(false);
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleRotate = async () => {
    if (!window.confirm("Rotate the URL? The current one will stop working immediately; you'll need to re-subscribe in any calendar app using it.")) return;
    setSubscribeLoading(true);
    try {
      const { data } = await api.post("/calendar/subscription-url/rotate");
      setSubscribeUrl(buildAbsoluteUrl(data.url || data.path));
      toast.success("Subscription URL rotated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not rotate URL");
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(subscribeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
      const response = await api.get(`/calendar/export?${buildQueryString()}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `visitor_calendar_${monthStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${MONTHS[month]} ${year} calendar PDF downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadList = async () => {
    setDownloadingList(true);
    try {
      const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
      const response = await api.get(`/visitors/list-pdf?${buildQueryString()}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `visitor_list_${monthStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Visitor list PDF downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "List download failed");
    } finally {
      setDownloadingList(false);
    }
  };

  const clearFilters = () => {
    setFilterDept("all");
    setFilterSpecialty("");
    setFilterTrainee("all");
  };

  const filterActive = filterDept !== "all" || !!filterSpecialty.trim() || filterTrainee !== "all";

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const todayStr = dateToStr(today);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);
  while (calendarDays.length % 7 !== 0) calendarDays.push(null);

  const getVisitorsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return visitorsWithPending.filter(v => isDateInRange(dateStr, v.arrival_date, v.departure_date));
  };

  // Phase 56 — Lease overlay: only render leases on days that ALSO have a
  // visitor scheduled, i.e. potential double-bookings. Otherwise the calendar
  // gets too busy. The full lease schedule is still available under /leases.
  const getLeasesForDay = (day) => {
    if (!day || !showLeases) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    // Skip the work if no visitors fall on this day
    const dayVisitors = visitorsWithPending.filter(v => isDateInRange(dateStr, v.arrival_date, v.departure_date));
    if (dayVisitors.length === 0) return [];
    return leases.filter(lz => {
      if (!lz.start_date || lz.start_date > dateStr) return false;
      if (lz.end_date && lz.end_date < dateStr) return false;
      return true;
    });
  };

  return (
    <TooltipProvider>
      <div className="space-y-6 fade-in" data-testid="visitor-calendar-page">
        {/* Phase 56 — sticky save bar for drag-and-drop date moves */}
        {pendingCount > 0 && (
          <div className="sticky top-0 z-40 -mx-4 md:-mx-8 lg:-mx-12 px-4 md:px-8 lg:px-12 py-3 bg-amber-50 border-y border-amber-300 flex items-center justify-between gap-3 flex-wrap" data-testid="calendar-save-bar">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <Save className="h-4 w-4" />
              <span><strong data-testid="calendar-pending-count">{pendingCount}</strong> unsaved move{pendingCount === 1 ? "" : "s"}</span>
              <span className="text-xs text-amber-800/80 hidden sm:inline">— drag visitors to new dates, then save when ready.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={discardPending} disabled={saving} className="border-amber-300 text-amber-900 hover:bg-amber-100" data-testid="calendar-discard-btn">
                <Undo2 className="h-3.5 w-3.5 mr-1" /> Discard
              </Button>
              <Button size="sm" onClick={savePending} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="calendar-save-btn">
                <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : `Save ${pendingCount} move${pendingCount === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        )}
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58] hover:text-[#1C1F1E]">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Visitor Calendar
            </h1>
            <p className="text-[#525C58] mt-1">Overview of visiting professionals schedule</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openSubscribeDialog}
              className="border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors"
              data-testid="calendar-subscribe-btn"
            >
              <Calendar className="h-4 w-4 mr-2" /> Subscribe
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors"
              data-testid="calendar-download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" /> {downloading ? "Preparing…" : "Download PDF"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadList}
              disabled={downloadingList}
              className="border-[#1B4332]/20 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors"
              data-testid="visitor-list-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-2" /> {downloadingList ? "Preparing…" : "Visitor List PDF"}
            </Button>
            <Button variant="outline" size="sm" onClick={goToday} className="border-[#E8E8E6]" data-testid="today-btn">
              Today
            </Button>
            <Button variant="outline" size="icon" onClick={prevMonth} className="border-[#E8E8E6] h-9 w-9" data-testid="prev-month-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-medium text-[#1C1F1E] min-w-[180px] text-center" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="calendar-month-label">
              {MONTHS[month]} {year}
            </span>
            <Button variant="outline" size="icon" onClick={nextMonth} className="border-[#E8E8E6] h-9 w-9" data-testid="next-month-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Phase 50 — Filter bar */}
        <Card className="border-[#E8E8E6]" data-testid="calendar-filter-bar">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#7A8A84] font-medium">
                <Filter className="h-3.5 w-3.5" /> Filters
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#525C58]">Department</span>
                <Select value={filterDept} onValueChange={setFilterDept}>
                  <SelectTrigger className="h-9 w-[180px]" data-testid="filter-department-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d.id || d} value={d.key || d.name || d}>{d.name || d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#525C58]">Specialty</span>
                <Input
                  className="h-9 w-[180px]"
                  placeholder="e.g. Pediatrics"
                  value={filterSpecialty}
                  onChange={(e) => setFilterSpecialty(e.target.value)}
                  data-testid="filter-specialty-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#525C58]">Level</span>
                <Select value={filterTrainee} onValueChange={setFilterTrainee}>
                  <SelectTrigger className="h-9 w-[140px]" data-testid="filter-trainee-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All levels</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="resident">Resident</SelectItem>
                    <SelectItem value="faculty">Faculty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {filterActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-[#525C58] hover:text-[#1B4332] h-9"
                  data-testid="clear-filters-btn"
                >
                  Clear filters
                </Button>
              )}
              {/* Phase 56 — leases overlay toggle */}
              <label className="ml-2 flex items-center gap-1.5 text-xs text-[#1C1F1E] cursor-pointer select-none" data-testid="show-leases-toggle">
                <input
                  type="checkbox"
                  checked={showLeases}
                  onChange={(e) => setShowLeases(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                  data-testid="show-leases-checkbox"
                />
                <KeyRound className="h-3 w-3 text-amber-700" /> Flag lease conflicts ({leases.length} active lease{leases.length === 1 ? "" : "s"})
              </label>
              <span className="ml-auto text-xs text-[#7A8A84]" data-testid="filter-result-count">
                {visitors.length} visitor{visitors.length === 1 ? "" : "s"} this month
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap gap-3" data-testid="calendar-legend">
          {Object.entries(DEPT_COLORS).map(([dept, colors]) => (
            <div key={dept} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${colors.bg}`} />
              <span className="text-xs text-[#525C58]">{dept}</span>
            </div>
          ))}
          <div className="ml-4 flex items-center gap-3">
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-xs text-[#7A8A84]">Approved</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-purple-500" /><span className="text-xs text-[#7A8A84]">Housing</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-xs text-[#7A8A84]">CMO Review</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" /><span className="text-xs text-[#7A8A84]">Dept Review</span></div>
          </div>
          <div className="ml-2 flex items-center gap-3" data-testid="level-legend">
            <span className="text-xs uppercase tracking-wider text-[#7A8A84] font-medium">Level</span>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-3 bg-amber-600" /><span className="text-xs text-[#7A8A84]">Student</span></div>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-3 bg-blue-700" /><span className="text-xs text-[#7A8A84]">Resident</span></div>
            <div className="flex items-center gap-1.5"><div className="w-1.5 h-3 bg-emerald-700" /><span className="text-xs text-[#7A8A84]">Faculty</span></div>
          </div>
        </div>

        <Card className="border-[#E8E8E6]" data-testid="calendar-grid">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="w-6 h-6 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div>
                {/* Day headers */}
                <div className="grid grid-cols-7 border-b border-[#E8E8E6]">
                  {DAYS.map(d => (
                    <div key={d} className="p-3 text-center text-sm font-medium text-[#7A8A84]">{d}</div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7">
                  {calendarDays.map((day, i) => {
                    const dayVisitors = getVisitorsForDay(day);
                    const dateStr = day ? `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : "";
                    const isToday = dateStr === todayStr;

                    return (
                      <div
                        key={i}
                        className={`min-h-[100px] border-b border-r border-[#E8E8E6] p-1.5 ${
                          day ? "bg-white hover:bg-[#FCFCFB]" : "bg-[#F4F4F1]/50"
                        } ${i % 7 === 0 ? "" : ""}`}
                        onDragOver={day ? onCellDragOver : undefined}
                        onDrop={day ? (e) => onCellDrop(e, dateStr) : undefined}
                        data-testid={day ? `day-cell-${dateStr}` : undefined}
                      >
                        {day && (
                          <>
                            <div className={`text-sm mb-1 ${isToday ? "bg-[#1B4332] text-white w-7 h-7 rounded-full flex items-center justify-center font-semibold" : "text-[#525C58] pl-1"}`}>
                              {day}
                            </div>
                            <div className="space-y-0.5">
                              {/* Phase 56 — leases (long-term tenants) — render first so they sit above visitor chips */}
                              {getLeasesForDay(day).slice(0, 2).map((lz) => (
                                <Tooltip key={lz.id}>
                                  <TooltipTrigger asChild>
                                    <div
                                      onClick={() => navigate(`/leases?edit=${lz.id}`, { state: { from: "/calendar" } })}
                                      className="text-[10px] px-1.5 py-0.5 cursor-pointer hover:opacity-80 transition-opacity truncate flex items-center gap-1 rounded bg-[repeating-linear-gradient(45deg,#fee2e2_0_4px,#fecaca_4px_8px)] text-red-900 border border-red-300"
                                      data-testid={`cal-lease-${lz.id}`}
                                    >
                                      <Home className="h-2.5 w-2.5 shrink-0" />
                                      <span className="truncate font-medium">{lz.unit_name}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="max-w-xs">
                                    <p className="font-semibold">{lz.unit_name} <span className="opacity-70">· Leased</span></p>
                                    <p className="text-xs">Tenant: {lz.tenant_name}</p>
                                    <p className="text-xs">{lz.start_date} → {lz.end_date || "open-ended"}</p>
                                    <p className="text-xs capitalize">Status: {lz.status}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                              {getLeasesForDay(day).length > 2 && (
                                <div className="text-[10px] text-red-700 pl-1">+{getLeasesForDay(day).length - 2} lease conflicts</div>
                              )}
                              {dayVisitors.slice(0, 3).map(v => {
                                const isGuest = v.kind === "guest";
                                const colors = DEPT_COLORS[v.department] || { bg: "bg-gray-200", text: "text-gray-800", border: "border-gray-300" };
                                const isArrival = v.arrival_date === dateStr;
                                const isDeparture = v.departure_date === dateStr;
                                const lvl = isGuest ? "faculty" : visitorLevel(v);
                                return (
                                  <Tooltip key={v.id}>
                                    <TooltipTrigger asChild>
                                      <div
                                        draggable={!isGuest}
                                        onDragStart={(e) => { if (!isGuest) onChipDragStart(e, v); }}
                                        onClick={() => {
                                          if (isGuest) {
                                            navigate(`/guest-requests/${v.id}`, { state: { from: "/calendar" } });
                                            return;
                                          }
                                          // Phase 56 — when a request has a linked booking (Phase 52 sync),
                                          // jump straight to the booking dialog so coordinators can edit
                                          // dates/unit/transport in one place. Otherwise fall back to the
                                          // request detail page. Either way carry `from=/calendar` in state
                                          // so the close button returns here.
                                          if (v.linked_booking_id) {
                                            navigate(`/bookings?edit=${v.linked_booking_id}`, { state: { from: "/calendar" } });
                                          } else {
                                            navigate(`/requests/${v.id}`, { state: { from: "/calendar" } });
                                          }
                                        }}
                                        className={`${colors.bg} ${colors.text} ${LEVEL_STRIPE[lvl] || ""} text-[10px] px-1.5 py-0.5 ${isGuest ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} hover:opacity-80 transition-opacity truncate flex items-center gap-1
                                          ${v._pending ? "ring-2 ring-amber-400 ring-offset-1" : ""}
                                          ${isArrival && isDeparture ? "rounded" : isArrival ? "rounded-l" : isDeparture ? "rounded-r" : ""}`}
                                        data-testid={`cal-visitor-${v.id}`}
                                        data-kind={isGuest ? "guest" : "visitor"}
                                        data-level={lvl}
                                        data-pending={v._pending ? "1" : undefined}
                                      >
                                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[v.status] || "bg-gray-400"}`} />
                                        <span className="truncate font-medium">{isGuest ? "\u{1F3E0} " : ""}{v.visitor_name}</span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-xs">
                                      <p className="font-semibold">{v.visitor_name} <span className="opacity-70">· {isGuest ? "Non-Medical Guest" : LEVEL_LABEL[lvl]}</span></p>
                                      <p className="text-xs">{v.specialty}{isGuest ? "" : ` - ${v.department}`}</p>
                                      <p className="text-xs">{v.arrival_date} to {v.departure_date}</p>
                                      <p className="text-xs">Party: {v.party_size} | Status: {v.status}</p>
                                      {v.created_by_name && <p className="text-xs">Submitted by: {v.created_by_name}</p>}
                                      {v.housing_approval?.housing_details && <p className="text-xs">Housing: {v.housing_approval.housing_details}</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                              {dayVisitors.length > 3 && (
                                <div className="text-[10px] text-[#7A8A84] pl-1">+{dayVisitors.length - 3} more</div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Visitors this month summary */}
        {visitors.length > 0 && (
          <Card className="border-[#E8E8E6]">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <CalendarDays className="h-5 w-5 inline mr-2 text-[#40916C]" />
                Visitors This Month ({visitors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="monthly-visitors-list">
                {visitors.map(v => {
                  const isGuest = v.kind === "guest";
                  const colors = DEPT_COLORS[v.department] || { bg: "bg-gray-100", text: "text-gray-800" };
                  const lvl = isGuest ? "faculty" : visitorLevel(v);
                  return (
                    <div
                      key={v.id}
                      onClick={() => navigate(isGuest ? `/guest-requests/${v.id}` : `/requests/${v.id}`)}
                      className={`p-3 border border-[#E8E8E6] rounded-lg cursor-pointer hover:shadow-sm transition-shadow ${isGuest ? "" : LEVEL_STRIPE[lvl]}`}
                      data-testid={`month-visitor-${v.id}`}
                      data-level={lvl}
                      data-kind={isGuest ? "guest" : "visitor"}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-[#1C1F1E]">{isGuest ? "\u{1F3E0} " : ""}{v.visitor_name}</span>
                        <Badge className={`${colors.bg} ${colors.text} border-0 text-[10px]`}>{v.department}</Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-[#525C58]">{v.specialty}</p>
                        {!isGuest && (
                          <span
                            className={`text-[10px] uppercase tracking-wide font-medium ${
                              lvl === "student" ? "text-amber-700" : lvl === "resident" ? "text-blue-700" : "text-emerald-700"
                            }`}
                            data-testid={`month-visitor-level-${v.id}`}
                          >
                            {LEVEL_LABEL[lvl]}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#7A8A84] mt-1">{v.arrival_date} &rarr; {v.departure_date} | Party: {v.party_size}</p>
                      {v.created_by_name && (
                        <p className="text-xs text-[#7A8A84] mt-1" data-testid={`cal-submitter-${v.id}`}>
                          Submitted by <span className="text-[#525C58]">{v.created_by_name}</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={subscribeOpen} onOpenChange={setSubscribeOpen}>
          <DialogContent className="max-w-xl" data-testid="subscribe-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Subscribe to visitor calendar
              </DialogTitle>
              <DialogDescription className="text-[#525C58]">
                Paste this URL into Google, Outlook or Apple Calendar. New approved
                visitors will appear automatically — no re-download required.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-[#7A8A84] uppercase tracking-wider">
                  Your personal subscription URL
                </label>
                <div className="flex gap-2">
                  <Input
                    value={subscribeLoading ? "Generating URL…" : subscribeUrl}
                    readOnly
                    onClick={(e) => e.target.select()}
                    className="font-mono text-xs"
                    data-testid="subscribe-url-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleCopy}
                    disabled={!subscribeUrl || subscribeLoading}
                    className="shrink-0 border-[#1B4332]/20"
                    data-testid="subscribe-copy-btn"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-[#7A8A84]">
                  Keep this URL private. Anyone with it can view your hospital&apos;s visitor calendar.
                </p>
              </div>

              <div className="rounded-lg border border-[#E8E8E6] bg-[#F4F4F1]/60 p-4 space-y-2">
                <p className="text-xs font-semibold text-[#1C1F1E] uppercase tracking-wider">How to subscribe</p>
                <ul className="text-sm text-[#525C58] space-y-1.5 list-disc pl-4">
                  <li><strong>Google Calendar</strong>: calendar.google.com → left sidebar → Other calendars → + → From URL → paste</li>
                  <li><strong>Apple Calendar (Mac/iPhone)</strong>: File → New Calendar Subscription → paste URL</li>
                  <li><strong>Outlook</strong>: Home → Add Calendar → Subscribe from web → paste URL</li>
                </ul>
                <p className="text-xs text-[#7A8A84] pt-1">
                  Most calendar apps refresh external feeds every 6–24 hours. The feed itself updates instantly on our side.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={handleRotate}
                disabled={subscribeLoading}
                className="border-amber-200 text-amber-800 hover:bg-amber-50"
                data-testid="subscribe-rotate-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Rotate URL
              </Button>
              <Button onClick={() => setSubscribeOpen(false)} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
